import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('click', this.#handleInstallmentsClick);
    this.addEventListener('click', this.#handleCheckoutClick);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.#openFromCartRedirect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('click', this.#handleInstallmentsClick);
    this.removeEventListener('click', this.#handleCheckoutClick);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.#resumeLenis();
  }

  /**
   * Opens the drawer after cart additions.
   * Quantity/remove updates from the cart itself must not re-trigger open.
   * @param {CustomEvent} event
   */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    const source = event && event.detail && event.detail.data ? event.detail.data.source : null;
    if (source === 'cart-items-component' || source === 'cart-switch-to-subscription') return;

    const dialog = this.refs && this.refs.dialog;
    if (dialog && dialog.open) return;

    this.showDialog();
  };

  /**
   * Close the drawer when Shop Pay "View sample plans" is clicked so the
   * installments popup is not trapped under the open dialog.
   * Uses composedPath so it works across the payment-terms shadow root.
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

    for (const node of path) {
      if (node && node.id === 'shopify-installments-cta') {
        this.closeDialog();
        return;
      }
    }
  };

  /**
   * Show a loading state on the checkout button while navigating to checkout.
   * @param {MouseEvent} event
   */
  #handleCheckoutClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.cart__checkout-button');
    if (!button || !this.contains(button)) return;
    if (button.getAttribute('aria-busy') === 'true' || button.disabled) {
      event.preventDefault();
      return;
    }

    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
  };

  /**
   * Pause Lenis while the drawer is open so trackpad wheel can scroll cart items.
   */
  #handleDialogOpen = () => {
    this.#pauseLenis();
  };

  /**
   * Resume Lenis after the drawer closes.
   */
  #handleDialogClose = () => {
    this.#resumeLenis();
  };

  #pauseLenis() {
    const lenis = window.lenis;
    if (lenis && typeof lenis.stop === 'function') {
      lenis.stop();
    }
  }

  #resumeLenis() {
    const lenis = window.lenis;
    if (lenis && typeof lenis.start === 'function') {
      lenis.start();
    }
  }

  /**
   * Opens the drawer when arriving from /cart via ?open_cart=1 redirect.
   */
  #openFromCartRedirect() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('open_cart') !== '1') return;

    params.delete('open_cart');
    const next = params.toString();
    const cleanUrl =
      window.location.pathname + (next ? `?${next}` : '') + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);

    requestAnimationFrame(() => {
      this.open();
    });
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
